import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { cn } from "@/lib/utils";
import { Pencil } from "lucide-react";
import { useCallback, useState } from "react";

/**
 * Hover-to-edit field — the confirmed admin edit model.
 *
 * Reads as plain text with a quiet pencil that surfaces on hover; click turns it
 * into an input in place; Enter/blur commits, Esc cancels. Session-local in the
 * design mock (the caller holds the value) — persistence is a dev follow-up.
 *
 * Shared by the candidate drawer and the role page so the edit affordance is
 * identical on every admin surface.
 */
export function EditableField({
  value,
  onCommit,
  multiline,
  placeholder,
  className,
}: {
  value: string;
  onCommit: (v: string) => void;
  multiline?: boolean;
  placeholder?: string;
  className?: string;
}) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState("");
  const focusOnMount = useCallback((el: HTMLInputElement | HTMLTextAreaElement | null) => {
    el?.focus();
  }, []);

  if (editing) {
    const commit = () => {
      setEditing(false);
      onCommit(draft.trim());
    };
    const base =
      "w-full rounded-md border bg-background px-2 py-1.5 text-sm text-foreground outline-none ring-2 ring-ring";
    return multiline ? (
      <textarea
        ref={focusOnMount}
        rows={3}
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        onBlur={commit}
        onKeyDown={(e) => {
          if (e.key === "Escape") setEditing(false);
        }}
        className={cn(base, "resize-none")}
      />
    ) : (
      <input
        ref={focusOnMount}
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        onBlur={commit}
        onKeyDown={(e) => {
          if (e.key === "Enter") commit();
          if (e.key === "Escape") setEditing(false);
        }}
        className={base}
      />
    );
  }

  return (
    <button
      type="button"
      onClick={() => {
        setDraft(value);
        setEditing(true);
      }}
      className={cn(
        "group/edit -mx-2 flex w-[calc(100%+1rem)] items-start justify-between gap-2 rounded-md px-2 py-1 text-left text-sm outline-none transition-colors hover:bg-muted/60 focus-visible:ring-2 focus-visible:ring-ring",
        className,
      )}
    >
      <span className={cn("min-w-0 whitespace-pre-wrap", !value && "text-muted-foreground")}>
        {value || placeholder}
      </span>
      <Pencil
        className="mt-0.5 size-3 shrink-0 text-transparent transition-colors group-hover/edit:text-muted-foreground/70"
        aria-hidden
      />
    </button>
  );
}

/** Compact inline dropdown — the admin's stage / status / lifecycle controls. Renders the product's own
 *  popover menu (Radix Select), NOT the native OS dropdown, so the open menu matches the app on every
 *  surface. Default is a compact inline chip; pass `fullWidth` for a form field. */
export function MiniSelect({
  value,
  onChange,
  options,
  ariaLabel,
  className,
  fullWidth,
}: {
  value: string;
  onChange: (v: string) => void;
  options: { value: string; label: string }[];
  ariaLabel: string;
  className?: string;
  fullWidth?: boolean;
}) {
  return (
    <Select value={value} onValueChange={onChange}>
      <SelectTrigger
        aria-label={ariaLabel}
        className={cn(
          "shadow-none",
          fullWidth ? "h-9 w-full text-sm" : "h-7 w-auto gap-1.5 rounded-md bg-card px-2.5 py-1 text-xs font-medium",
          className,
        )}
      >
        <SelectValue />
      </SelectTrigger>
      <SelectContent>
        {options.map((o) => (
          <SelectItem key={o.value} value={o.value}>
            {o.label}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}
